import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

import { api } from '../api/index.ts';
import { ApiError, type Me } from '../api/types.ts';
import { setLang, useT } from '../i18n/index.ts';
import { Icon } from '../ui/icons.tsx';
import { APP_NAME, Num, TAGLINE_UR } from '../ui/parts.tsx';

const RESEND_SECONDS = 60;
const TYPED_EMAIL_KEY = 'expense.signinEmail';

/** The email typed on the sign-in page, for this tab only (survives the update reload). */
function readTypedEmail(): string {
  try {
    return sessionStorage.getItem(TYPED_EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}

function keepTypedEmail(value: string): void {
  try {
    if (value) sessionStorage.setItem(TYPED_EMAIL_KEY, value);
    else sessionStorage.removeItem(TYPED_EMAIL_KEY);
  } catch {
    /* storage blocked: the field just starts empty after a reload */
  }
}
/** Whom someone without an account contacts (backend SALES_CONTACT). */
const SALES_CONTACT = 'kharcha@kcloudai.com';
/** Codes live 5 minutes (RULES.md rule 5); the countdown is a guide, the server decides. */
const CODE_SECONDS = 5 * 60;

/** Email → 6-digit code (SPEC §4). Never says whether the email exists. */
export function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const { t } = useT();
  // Kept for this tab, so the page reloading for a new release doesn't clear what was typed.
  const [email, setEmailState] = useState(() => readTypedEmail());
  const setEmail = (value: string) => {
    setEmailState(value);
    keepTypedEmail(value);
  };
  const [phase, setPhase] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The email has no account in any company / house: say so on the email step, with the contact.
  const [notRegistered, setNotRegistered] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  useEffect(() => {
    if (expiresIn <= 0) return;
    const id = window.setTimeout(() => setExpiresIn((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [expiresIn]);

  async function sendCode() {
    setBusy(true);
    setError(null);
    setNotRegistered(false);
    try {
      await api.requestOtp(email.trim().toLowerCase());
      setPhase('code');
      setCode('');
      setResendIn(RESEND_SECONDS);
      setExpiresIn(CODE_SECONDS);
      window.setTimeout(() => codeInput.current?.focus(), 50);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'email_not_registered') {
        // Owner's decision (12 Oct 2026): stay on the email step and say whom to contact.
        setPhase('email');
        setNotRegistered(true);
      } else if (e instanceof ApiError && e.code === 'workspace_paused') {
        setError(t('login.paused'));
      } else if (e instanceof ApiError && e.code === 'rate_limited') {
        setError(t('login.tooMany'));
      } else {
        setError(t('app.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    setBusy(true);
    setError(null);
    try {
      const me = await api.verifyOtp(email.trim().toLowerCase(), value);
      keepTypedEmail('');
      onSignedIn(me);
    } catch (e) {
      const expired = e instanceof ApiError && (e.code === 'otp_expired' || e.code === 'otp_attempts_exceeded');
      if (expired) {
        setPhase('email');
        setError(t('login.expired'));
      } else {
        setError(t('login.wrongCode'));
        setCode('');
        codeInput.current?.focus();
      }
    } finally {
      setBusy(false);
    }
  }

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const label = 'text-[15px] font-bold text-ink lg:text-[clamp(14px,min(1.1vw,1.9vh),21px)] rtl:text-[16px]';
  const field =
    'flex h-14 lg:h-[clamp(46px,6.4vh,80px)] lg:text-[clamp(15px,min(1.2vw,2vh),22px)] items-center rounded-xl border-[1.5px] border-line-strong bg-paper px-4 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/15';
  const primary =
    'flex h-14 lg:h-[clamp(46px,6.4vh,80px)] lg:text-[clamp(15px,min(1.25vw,2.1vh),24px)] w-full items-center justify-center gap-2 rounded-xl bg-brand text-[17px] font-bold text-white transition-colors hover:bg-brand-dark disabled:opacity-45 disabled:hover:bg-brand rtl:text-[18px]';
  const note = 'm-0 text-[14px] leading-relaxed text-muted lg:text-[clamp(12px,min(1vw,1.55vh),19px)] rtl:text-[15px]';

  return (
    <div className="min-h-app bg-paper lg:grid lg:h-app lg:grid-cols-[minmax(0,2.2fr)_minmax(380px,1fr)] lg:overflow-hidden">
      <BrandPanel />

      {/* Phones and tablets: one column. Desktop: the white right-hand column. */}
      <div className="safe-top safe-bottom no-scrollbar flex min-h-app flex-col bg-paper lg:h-app lg:overflow-y-auto">
        <div className="flex justify-end px-5 pt-5 sm:px-8 sm:pt-7 lg:px-[clamp(24px,2.8vw,48px)] lg:pt-[clamp(16px,4vh,40px)]">
          <LangPills />
        </div>
        <div className="flex w-full flex-1 flex-col items-center justify-center px-5 pt-4 pb-10 sm:px-8 lg:items-stretch lg:px-[clamp(24px,2.8vw,48px)] lg:pt-[clamp(8px,2vh,16px)] lg:pb-[clamp(16px,4vh,40px)] short:pb-5">
          <div className="flex w-full max-w-[460px] flex-col gap-6 lg:max-w-[clamp(400px,30vw,640px)] lg:gap-[clamp(14px,2.6vh,32px)] short:gap-4">
            <header dir="ltr" className="flex flex-col gap-1 lg:hidden">
              <span className="font-sans text-[52px] leading-none font-extrabold tracking-[-0.045em] text-brand min-[400px]:text-[58px]">
                {APP_NAME}
              </span>
              <p dir="rtl" lang="ur" className="m-0 self-start font-nastaliq text-[24px] leading-[1.8] text-ink">
                {TAGLINE_UR}
              </p>
            </header>

            <div className="flex flex-col gap-2">
              <h1 className="m-0 text-[clamp(32px,9vw,40px)] leading-tight font-extrabold tracking-[-0.02em] text-ink lg:text-[clamp(28px,min(3vw,5.2vh),64px)]">
                {t('login.title')}
              </h1>
              <p className="m-0 text-[clamp(15px,4.3vw,17px)] leading-relaxed text-ink-soft lg:text-[clamp(14px,min(1.25vw,2.1vh),24px)] rtl:text-[clamp(16px,4.6vw,18px)]">
                {t('login.subtitle')}
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-xl bg-danger-tint px-3.5 py-2.5 text-[15px] font-semibold text-danger-text"
              >
                <Icon name="alert" size={18} className="mt-0.5" />
                {error}
              </div>
            )}
            {notRegistered && (
              // Short and friendly, in the page's small text size (owner's request, 12 Oct 2026).
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg bg-warning-tint px-3 py-2 text-[13px] leading-snug text-warning-text sm:text-sm rtl:text-[14px] sm:rtl:text-[15px]"
              >
                <Icon name="info" size={15} className="mt-px shrink-0" />
                {/* The address on its own line, never split in the middle. */}
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span>{t('login.notRegistered')}</span>
                  <a
                    href={`mailto:${SALES_CONTACT}`}
                    dir="ltr"
                    className="w-fit max-w-full truncate font-semibold whitespace-nowrap text-warning-text underline underline-offset-2"
                  >
                    {SALES_CONTACT}
                  </a>
                </span>
              </div>
            )}

            {phase === 'email' ? (
              <form
                className="flex flex-col gap-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (validEmail && !busy) void sendCode();
                }}
              >
                <label className="flex flex-col gap-2">
                  <span className={label}>{t('login.workEmail')}</span>
                  <span className={field}>
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      dir="ltr"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setNotRegistered(false); // a new email: the old message no longer applies
                      }}
                      placeholder={t('login.emailPh')}
                      className="h-full min-w-0 flex-1 bg-transparent text-[17px] outline-none placeholder:text-key lg:text-[length:inherit]"
                    />
                  </span>
                </label>
                <button type="submit" disabled={!validEmail || busy} className={primary}>
                  {t('login.sendCode')}
                </button>
              </form>
            ) : (
              <form
                className="flex flex-col gap-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (code.length === 6 && !busy) void verify(code);
                }}
              >
                <div className="flex flex-col gap-2">
                  <span className={label}>{t('login.workEmail')}</span>
                  <div className={`${field} gap-2 pe-1`}>
                    <span dir="ltr" className="min-w-0 flex-1 truncate text-start text-[16px]">
                      {email.trim()}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPhase('email')}
                      className="h-12 shrink-0 rounded-lg px-3 text-[15px] font-bold text-brand hover:bg-brand-tint"
                    >
                      {t('login.change')}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-3">
                    <label htmlFor="otp" className={label}>
                      {t('login.digitCode')}
                    </label>
                    <span className="text-[14px] text-muted rtl:text-[15px]" aria-live="off">
                      {expiresIn > 0 ? (
                        <>
                          {t('login.expiresIn')} <Num className="font-bold text-ink">{clock(expiresIn)}</Num>
                        </>
                      ) : (
                        <span className="text-danger-text">{t('login.expired5')}</span>
                      )}
                    </span>
                  </div>
                  <CodeSlots
                    inputRef={codeInput}
                    value={code}
                    onChange={(v) => {
                      setCode(v);
                      if (v.length === 6) void verify(v);
                    }}
                  />
                </div>

                {/* Never says whether the email has an account (RULES.md rule 5): while waiting, "a code
                    is on its way if…"; once Send again is open, what to check when nothing came. */}
                <p className="m-0 text-[15px] leading-relaxed text-muted rtl:text-[16px]" aria-live="polite">
                  {resendIn > 0 ? t('login.sentNow') : t('login.noCodeHelp')}{' '}
                  {resendIn > 0 ? (
                    <span className="text-ink-soft">{t('login.sendAgainIn', { time: clock(resendIn) })}</span>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void sendCode()}
                      className="inline-flex min-h-12 items-center font-bold text-brand underline underline-offset-4 align-middle"
                    >
                      {t('login.sendAgain')}
                    </button>
                  )}
                </p>

                <button type="submit" disabled={code.length !== 6 || busy} className={primary}>
                  {t('login.signIn')}
                </button>
              </form>
            )}

            <div className="flex flex-col gap-2 pt-1">
              <p className={note}>{t('login.noPassword')}</p>
              <p className={note}>
                <span className="sm:hidden">{t('login.staysPhone')}</span>
                <span className="hidden sm:inline">{t('login.staysBrowser')}</span>
              </p>
              <p className={note}>{t('login.firstTime')}</p>
            </div>

            <p className="m-0 flex items-start gap-2.5 text-[14px] leading-relaxed text-muted lg:hidden rtl:text-[15px]">
              <Icon name="pin" size={18} className="mt-0.5 shrink-0" />
              {t('login.locationShort')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
/** m:ss for the countdowns. */
function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * Six digit slots drawn over ONE real input (transparent, on top), so paste, the keyboard's
 * one-time-code suggestion and screen readers all see a normal field.
 */
function CodeSlots({
  value,
  onChange,
  inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div className="relative" dir="ltr">
      <div className="grid grid-cols-6 gap-2.5 sm:gap-3" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => {
          // The next slot to fill is teal (the field is focused as the step opens).
          const active = i === Math.min(value.length, 5) && (focused || value.length < 6);
          return (
            <span
              key={i}
              className={`num flex h-16 items-center justify-center border-b-4 text-[28px] font-bold text-ink transition-colors ${
                active ? 'border-brand' : 'border-[#C9D3CE]'
              }`}
            >
              {value[i] ?? ''}
            </span>
          );
        })}
      </div>
      <input
        id="otp"
        ref={inputRef}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        className="absolute inset-0 size-full bg-transparent text-transparent caret-transparent outline-none selection:bg-transparent"
      />
    </div>
  );
}

/** English / اردو choice, each written in its own script. */
function LangPills() {
  const { lang } = useT();
  const pill = (on: boolean) =>
    `h-11 rounded-full px-5 text-[16px] font-bold transition-colors ${
      on ? 'bg-ink text-white' : 'border-[1.5px] border-[#C9D3CE] bg-transparent text-ink hover:bg-paper'
    }`;
  return (
    <div className="flex gap-2" role="group">
      <button
        type="button"
        aria-pressed={lang === 'en'}
        lang="en"
        onClick={() => setLang('en')}
        className={`${pill(lang === 'en')} font-sans`}
      >
        English
      </button>
      <button
        type="button"
        aria-pressed={lang === 'ur'}
        lang="ur"
        onClick={() => setLang('ur')}
        className={`${pill(lang === 'ur')} font-urdu text-[18px]`}
      >
        اردو
      </button>
    </div>
  );
}

/**
 * Desktop left side: the brand, the Urdu strap line, what Kharcha does, and one sample entry.
 * Nothing here is real data: before sign-in the server tells us nothing.
 * Every size follows the screen (width and height, between a floor and a ceiling), so the whole
 * panel fits without a scrollbar from small laptops to large monitors.
 */
function BrandPanel() {
  const { t, lang } = useT();
  const points: [string, string][] = [
    [t('login.point1Title'), t('login.point1Body')],
    [t('login.point2Title'), t('login.point2Body')],
    [t('login.point3Title'), t('login.point3Body')],
    [t('login.point4Title'), t('login.point4Body')],
  ];
  return (
    <aside className="no-scrollbar hidden bg-[#24665A] text-white lg:sticky lg:top-0 lg:flex lg:h-app lg:flex-col lg:justify-between lg:gap-[clamp(16px,3vh,40px)] lg:overflow-y-auto lg:px-[clamp(32px,4.6vw,120px)] lg:py-[clamp(24px,6vh,96px)]">
      <div className="grid gap-[clamp(24px,3.5vw,56px)] xl:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] xl:items-start">
        <div className="flex max-w-[clamp(28rem,34vw,46rem)] flex-col gap-[clamp(12px,2.6vh,36px)]">
          <div className="flex flex-col gap-[clamp(4px,1.2vh,12px)]">
            <span
              dir="ltr"
              className="font-sans text-[clamp(52px,min(7.4vw,11.5vh),176px)] leading-[0.9] font-extrabold tracking-[-0.045em]"
            >
              {APP_NAME}
            </span>
            <p
              dir="rtl"
              lang="ur"
              className={`m-0 font-nastaliq text-[clamp(20px,min(2.4vw,3.6vh),50px)] leading-relaxed text-white ${lang === 'ur' ? 'self-end' : 'self-start'}`}
            >
              {TAGLINE_UR}
            </p>
          </div>
          <p className="m-0 text-[clamp(16px,min(1.75vw,2.7vh),36px)] leading-snug font-medium text-white/95 rtl:leading-relaxed">
            {t('login.pitch')}
          </p>
          <ul className="m-0 flex list-none flex-col gap-[clamp(8px,2vh,20px)] p-0">
            {points.map(([title, body]) => (
              <li key={title} className="flex gap-[clamp(8px,0.9vw,14px)]">
                <Icon name="check" size={20} stroke={2.75} className="mt-[0.2em] size-[clamp(16px,2vh,28px)] shrink-0 text-[#F2C14E]" />
                <p className="m-0 text-[clamp(13px,min(1.2vw,1.85vh),24px)] leading-relaxed text-white/85 rtl:text-[clamp(14px,min(1.3vw,2vh),25px)]">
                  <strong className="font-bold text-white">{title}</strong> {body}
                </p>
              </li>
            ))}
          </ul>
        </div>
        {/* Two examples side by side with the text (wide screens only): an entry, and the
            owner's morning view on a tall enough screen. */}
        <div className="hidden flex-col gap-[clamp(14px,3vh,36px)] xl:flex">
          <SampleEntry />
          <OwnerMorning />
        </div>
      </div>

      <p className="m-0 text-[clamp(12px,1.6vh,20px)] text-white/85">
        {t('login.productOf')}{' '}
        <a
          dir="ltr"
          href="https://kcloudai.com"
          target="_blank"
          rel="noreferrer"
          className="font-bold text-white underline decoration-white/60 underline-offset-4 hover:decoration-white"
        >
          KCloudAI.com
        </a>
      </p>
    </aside>
  );
}

/**
 * "This is one entry in Kharcha": a slightly tilted example of what an owner reviews. Sized in
 * em from one font size that follows the screen, so the whole card shrinks and grows together;
 * on very short screens it steps aside so the text above fits.
 */
function SampleEntry() {
  const { t } = useT();
  const dashed = 'border-0 border-t-[1.5px] border-dashed border-line';
  const chip = 'inline-flex h-[2.5em] items-center rounded-[0.5em] px-[1em] text-[0.94em]';
  return (
    <figure className="m-0 hidden flex-col gap-[0.75em] text-[clamp(10px,min(1vw,1.25vh),21px)] xl:pt-1 [@media(min-height:620px)]:flex">
      <figcaption className="text-[0.94em] font-semibold text-white/90">{t('login.sampleLabel')}</figcaption>
      <div
        aria-hidden="true"
        className="-rotate-[1.6deg] rounded-[0.75em] bg-paper px-[1.5em] py-[1.25em] text-ink shadow-[0_24px_48px_rgba(7,32,28,0.35)] rtl:rotate-[1.6deg]"
      >
        <div className="flex items-start justify-between gap-[1em]">
          <span className="flex flex-col gap-[0.15em]">
            <span className="text-[0.82em] text-muted">{t('login.sampleSpentBy')}</span>
            <strong className="text-[1.06em]">{t('login.sampleWho')}</strong>
          </span>
          <span className="flex flex-col items-end gap-[0.15em] text-end">
            <span className="text-[0.82em] text-muted">{t('login.sampleWhen')}</span>
            <strong className="text-[0.94em]">{t('login.samplePortfolio')}</strong>
          </span>
        </div>
        <hr className={`${dashed} my-[1em]`} />
        <div className="flex flex-col gap-[0.6em]">
          <span className="flex justify-between gap-[1em]">
            <span>{t('login.sampleItem1')}</span>
            <Num>Rs 1,850</Num>
          </span>
          <span className="flex justify-between gap-[1em]">
            <span>{t('login.sampleItem2')}</span>
            <Num>Rs 620</Num>
          </span>
          <span className="flex justify-between gap-[1em]">
            <span>{t('login.sampleItem3')}</span>
            <Num>Rs 180</Num>
          </span>
          <span className="flex justify-between gap-[1em]">
            <span>{t('login.sampleItem4')}</span>
            <Num>Rs 350</Num>
          </span>
          <span className="mt-[0.35em] flex justify-between gap-[1em] text-[1.2em] font-extrabold">
            <span>{t('login.sampleTotal')}</span>
            <Num>Rs 3,000</Num>
          </span>
        </div>
        <hr className={`${dashed} my-[1em]`} />
        <div className="flex items-center gap-[0.9em]">
          <span className="flex size-[3.4em] shrink-0 items-center justify-center rounded-[0.5em] border-[1.5px] border-line bg-ground text-brand">
            <Icon name="camera" size={22} className="size-[1.4em]" />
          </span>
          <span className="flex flex-col gap-[0.15em]">
            <strong>{t('login.sampleReceipt')}</strong>
            <span className="text-[0.88em] text-muted">{t('login.sampleReceiptBy')}</span>
          </span>
        </div>
        <hr className={`${dashed} my-[1em]`} />
        <span className="inline-flex rounded-full bg-warning-tint px-[0.9em] py-[0.35em] text-[0.88em] font-bold text-warning-text">
          {t('login.sampleWaiting')}
        </span>
        <div className="mt-[0.9em] flex flex-wrap gap-[0.5em]">
          <span className={`${chip} border-[1.5px] border-line-strong font-semibold`}>{t('login.sampleFix')}</span>
          <span className={`${chip} border-[1.5px] border-danger/40 font-semibold text-danger`}>{t('login.sampleReject')}</span>
          <span className={`${chip} bg-brand font-bold text-white`}>{t('login.sampleApprove')}</span>
        </div>
      </div>
    </figure>
  );
}
/**
 * "The owner's morning": what an owner sees first in one portfolio. Like the sample entry, sized
 * in em from one screen-following font size; shown only when the screen is tall enough for both.
 */
function OwnerMorning() {
  const { t } = useT();
  const tile = 'flex flex-col gap-[0.2em] rounded-[0.6em] px-[0.9em] py-[0.7em]';
  return (
    <figure className="m-0 hidden flex-col gap-[0.75em] text-[clamp(10px,min(1vw,1.25vh),21px)] [@media(min-height:720px)]:flex">
      <figcaption className="text-[0.94em] font-semibold text-white/90">{t('login.morningLabel')}</figcaption>
      <div
        aria-hidden="true"
        className="rotate-[1.4deg] rounded-[0.75em] bg-paper px-[1.25em] py-[1.1em] text-ink shadow-[0_24px_48px_rgba(7,32,28,0.35)] rtl:-rotate-[1.4deg]"
      >
        <div className="flex items-baseline justify-between gap-[1em]">
          <strong className="text-[1.06em]">{t('login.morningPortfolio')}</strong>
          <span className="text-[0.82em] text-muted">{t('login.morningDate')}</span>
        </div>
        <div className="mt-[0.9em] grid grid-cols-2 gap-[0.6em]">
          <span className={`${tile} bg-ground`}>
            <span className="text-[0.82em] text-muted">{t('login.morningSpent')}</span>
            <Num className="text-[1.15em] font-extrabold">Rs 2,387</Num>
          </span>
          <span className={`${tile} bg-ground`}>
            <span className="text-[0.82em] text-muted">{t('login.morningCash')}</span>
            <Num className="text-[1.15em] font-extrabold text-brand">Rs 9,204</Num>
          </span>
          <span className={`${tile} bg-ink text-white`}>
            <span className="text-[0.82em] text-white/75">{t('login.morningReview')}</span>
            <strong className="text-[1.15em]">{t('login.morningReviewCount')}</strong>
          </span>
          <span className={`${tile} bg-warning-tint`}>
            <span className="text-[0.82em] text-warning-text/80">{t('login.morningNoReceipt')}</span>
            <strong className="text-[1.15em] text-warning-text">{t('login.morningNoReceiptCount')}</strong>
          </span>
        </div>
        <hr className="my-[0.9em] border-0 border-t-[1.5px] border-line" />
        <div className="flex flex-col gap-[0.45em] text-[0.94em]">
          <span className="flex justify-between gap-[1em]">
            <span>
              <strong>{t('login.morningBilal')}</strong> <span className="text-muted">{t('login.morningRecharged')}</span>
            </span>
            <Num className="font-bold">Rs 9,204</Num>
          </span>
          <span className="flex justify-between gap-[1em]">
            <span>
              <strong>{t('login.morningHamza')}</strong> <span className="text-muted">{t('login.morningNoCash')}</span>
            </span>
            <Num className="font-bold text-danger">Rs 0</Num>
          </span>
        </div>
        <hr className="my-[0.9em] border-0 border-t-[1.5px] border-line" />
        <div className="flex flex-col items-start gap-[0.6em]">
          <span className="inline-flex h-[2.4em] items-center rounded-[0.5em] border-[1.5px] border-line-strong px-[0.9em] text-[0.88em] font-semibold">
            {t('login.morningApproveAll')}
          </span>
          <span className="inline-flex h-[2.4em] items-center rounded-[0.5em] bg-brand px-[1em] text-[0.88em] font-bold text-white">
            {t('login.morningGiveCash')}
          </span>
        </div>
      </div>
    </figure>
  );
}
const NOTICE_KEY = 'expense.locationNoticeSeen';

/** SPEC §6: spenders are told on first launch that location is recorded with each entry. */
export function LocationNotice({ userId, children }: { userId: string; children: ReactNode }) {
  const { t } = useT();
  const key = `${NOTICE_KEY}.${userId}`;
  const [seen, setSeen] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  if (seen) return <>{children}</>;
  return (
    <div className="safe-top safe-bottom mx-auto flex min-h-app w-full max-w-md flex-col justify-center gap-5 bg-paper px-6 py-6 text-center short:gap-3">
      <span className="mx-auto flex size-20 items-center justify-center rounded-full bg-brand-tint text-brand">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      </span>
      <h1 className="m-0 text-2xl font-bold">{t('login.locationTitle')}</h1>
      <p className="m-0 text-[17px] leading-relaxed text-muted">{t('login.locationBody')}</p>
      <button
        type="button"
        onClick={() => {
          try {
            localStorage.setItem(key, '1');
          } catch {
            /* ignore */
          }
          setSeen(true);
        }}
        className="h-14 rounded-xl bg-brand text-[17px] font-bold text-white hover:bg-brand-dark"
      >
        {t('login.ok')}
      </button>
    </div>
  );
}
